import React, { useState, useEffect } from 'react';
import { GalleryEvent, GalleryImage } from '../../types/Gallery';
import { Choir } from '../../types/Choir';
import { apiClient } from '../../config/api';
import { AdminFormProps } from '../shared/AdminInterfaces';

interface GalleryEventFormData {
  title: string;
  description: string;
  date: string;
  eventType: 'Concert' | 'Competition' | 'Fundraiser' | 'Community Event' | 'Rehearsal' | 'Other';
  participatingChoirs: string[];
  location: string;
  publicGallery: boolean;
}

const GalleryEventForm: React.FC<AdminFormProps<GalleryEvent, GalleryEventFormData>> = ({
  item,
  isOpen,
  onSubmit,
  onClose,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<GalleryEventFormData>({
    title: '',
    description: '',
    date: '',
    eventType: 'Concert',
    participatingChoirs: [],
    location: '',
    publicGallery: true
  });

  const [choirs, setChoirs] = useState<Choir[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load choirs for the participating choirs dropdown
  useEffect(() => {
    const loadChoirs = async () => {
      try {
        const choirData = await apiClient.get('/api/public/choirs');
        setChoirs(choirData);
      } catch (error) {
        console.error('Failed to load choirs:', error);
      }
    };

    if (isOpen) {
      loadChoirs();
    }
  }, [isOpen]);

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title,
        description: item.description || '',
        date: new Date(item.date).toISOString().slice(0, 16),
        eventType: item.eventType,
        participatingChoirs: item.participatingChoirs || [],
        location: item.location || '',
        publicGallery: item.publicGallery
      });
    } else {
      setFormData({
        title: '',
        description: '',
        date: '',
        eventType: 'Concert',
        participatingChoirs: [],
        location: '',
        publicGallery: true
      });
    }
    setErrors({});
  }, [item]);

  const handleInputChange = (field: keyof GalleryEventFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleChoirToggle = (choirId: string) => {
    setFormData(prev => ({
      ...prev,
      participatingChoirs: prev.participatingChoirs.includes(choirId)
        ? prev.participatingChoirs.filter(id => id !== choirId)
        : [...prev.participatingChoirs, choirId]
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.eventType) {
      newErrors.eventType = 'Event type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {item ? 'Edit Gallery Event' : 'Add New Gallery Event'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`mt-1 block w-full border rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
              />
            </div>

            {/* Date */}
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                Date *
              </label>
              <input
                type="datetime-local"
                id="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                className={`mt-1 block w-full border rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.date ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
            </div>

            {/* Event Type */}
            <div>
              <label htmlFor="eventType" className="block text-sm font-medium text-gray-700">
                Event Type *
              </label>
              <select
                id="eventType"
                value={formData.eventType}
                onChange={(e) => handleInputChange('eventType', e.target.value as GalleryEventFormData['eventType'])}
                className={`mt-1 block w-full border rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.eventType ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={isLoading}
              >
                <option value="Concert">Concert</option>
                <option value="Competition">Competition</option>
                <option value="Fundraiser">Fundraiser</option>
                <option value="Community Event">Community Event</option>
                <option value="Rehearsal">Rehearsal</option>
                <option value="Other">Other</option>
              </select>
              {errors.eventType && <p className="mt-1 text-sm text-red-600">{errors.eventType}</p>}
            </div>

            {/* Location */}
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                Location
              </label>
              <input
                type="text"
                id="location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
              />
            </div>

            {/* Participating Choirs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Participating Choirs
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                {choirs.map((choir) => (
                  <label key={choir.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.participatingChoirs.includes(choir.id)}
                      onChange={() => handleChoirToggle(choir.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={isLoading}
                    />
                    <span className="ml-2 text-sm text-gray-700">{choir.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Visibility */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.publicGallery}
                  onChange={(e) => handleInputChange('publicGallery', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={isLoading}
                />
                <span className="ml-2 text-sm text-gray-700">Make this event public</span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : (item ? 'Update Event' : 'Create Event')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GalleryEventForm;
